import React, { useState, useEffect } from 'react';
import { Package, Settings, DollarSign, TrendingUp, Save, Plus, Edit2, Trash2, Eye, EyeOff, AlertCircle, Smartphone, Monitor } from 'lucide-react';
import { API_URL } from '../../config';
import { toast } from 'sonner';
import PackageCard from '../shared/PackageCard';

export default function PackageManagement({ adminToken }) {
  const [pricingSettings, setPricingSettings] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSettings, setEditingSettings] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Pricing Settings
      const settingsRes = await fetch(`${API_URL}/api/pricing/admin/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setPricingSettings(data);
      }
      
      // Packages
      const packagesRes = await fetch(`${API_URL}/api/pricing/admin/packages`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        setPackages(data.packages || []);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const updatePricingSettings = async (updates) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        toast.success('Pricing Settings aktualisiert');
        fetchData();
        setEditingSettings(false);
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const createPackage = async (packageData) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/packages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(packageData)
      });

      if (res.ok) {
        toast.success('Paket erstellt');
        setShowCreateForm(false); // Schließe Modal SOFORT
        await fetchData(); // Dann lade Daten neu
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      console.error('Error creating package:', error);
      toast.error('Fehler beim Erstellen');
    }
  };

  const updatePackage = async (packageId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/packages/${packageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        toast.success('Paket aktualisiert');
        fetchData();
        setEditingPackage(null);
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating package:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const deletePackage = async (packageId) => {
    if (!window.confirm('Paket wirklich löschen?')) return;

    try {
      const res = await fetch(`${API_URL}/api/pricing/admin/packages/${packageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (res.ok) {
        toast.success('Paket gelöscht');
        fetchData();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      
      {/* Header */}
      <div className="space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2 md:gap-3">
            <Package className="w-6 h-6 md:w-8 md:h-8 text-[#10B981]" />
            Paket-Management
          </h2>
          <p className="text-white/60 text-sm mt-1">
            Vollständig dynamisches Preis- & Paketsystem auf EURO-Basis
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateForm(true)}
          className="w-full md:w-auto px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center justify-center gap-2"
          data-testid="create-package-button"
        >
          <Plus className="w-5 h-5" />
          Neues Paket
        </button>
      </div>

      {/* Pricing Settings Card */}
      <div className="p-4 md:p-6 bg-black border-2 border-[#10B981]/30 rounded-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Settings className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
            <h3 className="text-lg md:text-xl font-bold text-white">Globale Preis-Einstellungen</h3>
          </div>
          
          {!editingSettings ? (
            <button
              onClick={() => setEditingSettings(true)}
              className="w-full sm:w-auto px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all flex items-center justify-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Bearbeiten
            </button>
          ) : null}
        </div>

        {pricingSettings && (
          <PricingSettingsForm
            settings={pricingSettings}
            editing={editingSettings}
            onSave={(updates) => {
              updatePricingSettings(updates);
              setEditingSettings(false);
            }}
            onCancel={() => {
              setEditingSettings(false);
            }}
          />
        )}
      </div>

      {/* Packages List */}
      <div className="space-y-4">
        <h3 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
          <Package className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
          Alle Pakete ({packages.length})
        </h3>

        {packages.map((pkg) => (
          <PackageListItem
            key={pkg.id}
            package={pkg}
            pricing={pricingSettings}
            isEditing={false}
            onEdit={() => {
              setEditingPackage(pkg);
              setShowEditModal(true);
            }}
            onCancelEdit={() => setEditingPackage(null)}
            onSave={(updates) => updatePackage(pkg.id, updates)}
            onDelete={() => deletePackage(pkg.id)}
          />
        ))}

        {packages.length === 0 && (
          <div className="p-12 text-center text-white/40">
            <Package className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p>Noch keine Pakete erstellt</p>
            <p className="text-sm mt-2">Klicke auf &quot;Neues Paket&quot; um zu starten</p>
          </div>
        )}
      </div>

      {/* Create Package Modal */}
      {showCreateForm && (
        <CreatePackageModal
          pricing={pricingSettings}
          onClose={() => setShowCreateForm(false)}
          onSave={createPackage}
        />
      )}

      {/* Edit Package Modal */}
      {showEditModal && editingPackage && (
        <EditPackageModal
          pkgData={editingPackage}
          pricing={pricingSettings}
          onClose={() => {
            setShowEditModal(false);
            setEditingPackage(null);
          }}
          onSave={(updates) => {
            updatePackage(editingPackage.id, updates);
            setShowEditModal(false);
            setEditingPackage(null);
          }}
        />
      )}

    </div>
  );
}


// ============================================================================
// PRICING SETTINGS FORM
// ============================================================================

function PricingSettingsForm({ settings, editing, onSave, onCancel }) {
  const [form, setForm] = useState(settings);
  const [refreshingRate, setRefreshingRate] = useState(false);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const handleSave = () => {
    const updates = {};
    
    // Nur geänderte Werte
    Object.keys(form).forEach(key => {
      if (form[key] !== settings[key] && !['_id', 'updated_at', 'updated_by', 'exchange_rate_last_updated'].includes(key)) {
        updates[key] = form[key];
      }
    });

    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      toast.info('Keine Änderungen vorgenommen');
      onCancel();
    }
  };

  const handleCancel = () => {
    // Form auf Original-Werte zurücksetzen
    setForm(settings);
    onCancel();
  };

  const handleRefreshExchangeRate = async () => {
    setRefreshingRate(true);
    try {
      const adminToken = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/pricing/admin/refresh-exchange-rate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setForm({...form, usd_to_eur_rate: data.usd_to_eur_rate, exchange_rate_last_updated: data.last_updated});
        toast.success(`Wechselkurs aktualisiert: ${data.usd_to_eur_rate}`);
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Exchange rate error:', error);
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setRefreshingRate(false);
    }
  };

  // Hilfsfunktion: USD → EUR umrechnen
  const usdToEur = (usd) => {
    return (usd * (form.usd_to_eur_rate || 0.90)).toFixed(2);
  };

  // Hilfsfunktion: Marge berechnen
  const calculateMargin = (costUsd, saleEur) => {
    const costEur = parseFloat(usdToEur(costUsd));
    const margin = saleEur - costEur;
    return margin.toFixed(2);
  };

  return (
    <div className="space-y-8">
      
      {/* Wechselkurs */}
      <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-bold text-white block mb-1">USD → EUR Wechselkurs</label>
            <input
              type="number"
              step="0.01"
              value={form.usd_to_eur_rate}
              onChange={(e) => setForm({...form, usd_to_eur_rate: parseFloat(e.target.value)})}
              disabled={!editing}
              className="w-32 px-3 py-2 bg-black/60 border-2 border-white/20 rounded-lg text-white disabled:opacity-50 focus:border-[#10B981] transition-all"
            />
            {form.exchange_rate_last_updated && (
              <p className="text-xs text-white/40 mt-1">
                Stand: {new Date(form.exchange_rate_last_updated).toLocaleString('de-DE')}
              </p>
            )}
          </div>
          <button
            onClick={handleRefreshExchangeRate}
            disabled={refreshingRate}
            className="px-4 py-2 bg-[#10B981] text-black rounded-lg font-medium hover:bg-[#059669] transition disabled:opacity-50 flex items-center gap-2"
          >
            <span className={refreshingRate ? 'animate-spin' : ''}>🔄</span>
            {refreshingRate ? 'Lädt...' : 'Aktualisieren'}
          </button>
        </div>
      </div>
      
      {/* SNAP Conversion-Rate - ADMIN STEUERBAR */}
      <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <label className="text-sm font-bold text-white block mb-1">💎 SNAPs pro Euro (Conversion-Rate)</label>
            <input
              type="number"
              step="0.5"
              min="1"
              max="100"
              value={form.snaps_per_euro || 2}
              onChange={(e) => setForm({...form, snaps_per_euro: parseFloat(e.target.value)})}
              disabled={!editing}
              className="w-32 px-3 py-2 bg-black/60 border-2 border-white/20 rounded-lg text-white disabled:opacity-50 focus:border-cyan-400 transition-all"
            />
            <p className="text-xs text-white/60 mt-2">
              Beispiel: 2.0 = Nutzer bekommt 2 SNAPs pro 1€
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-white/40 mb-1">Aktuell:</p>
            <p className="text-2xl font-bold text-cyan-400">{form.snaps_per_euro || 2} SNAPs</p>
            <p className="text-xs text-white/40">pro 1€</p>
          </div>
        </div>
      </div>

      {/* 2-SPALTEN LAYOUT: Astria Kosten | SNAP Verkaufspreise */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* LINKS: ASTRIA KOSTEN */}
        <div className="space-y-6">
          <h4 className="text-lg font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-400" />
            Astria API Kosten (USD)
          </h4>
          
          {/* Avatar Training */}
          <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Avatar Training</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.avatar_cost_usd || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, avatar_cost_usd: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, avatar_cost_usd: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="2.25 oder 2,25"
            />
            <p className="text-xs text-orange-400 mt-2">
              ${form.avatar_cost_usd || 0} → €{usdToEur(form.avatar_cost_usd || 0)}
            </p>
          </div>

          {/* Shot */}
          <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Shot (Avatar Bild)</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.shot_cost_usd || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, shot_cost_usd: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, shot_cost_usd: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="0.26 oder 0,26"
            />
            <p className="text-xs text-white/40 mt-1">Text-to-Image</p>
            <p className="text-xs text-orange-400 mt-2">
              ${form.shot_cost_usd || 0} → €{usdToEur(form.shot_cost_usd || 0)}
            </p>
          </div>

          {/* Remix */}
          <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Remix (Avatar Variation)</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.remix_cost_usd || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, remix_cost_usd: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0.35;
                setForm({...form, remix_cost_usd: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="0.35 oder 0,35"
            />
            <p className="text-xs text-white/40 mt-1">Image-to-Image (teurer!)</p>
            <p className="text-xs text-orange-400 mt-2">
              ${form.remix_cost_usd || 0} → €{usdToEur(form.remix_cost_usd || 0)}
            </p>
          </div>

          {/* Modify - NEU */}
          <div className="p-4 bg-black/40 border border-white/10 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Modify (Avatar + Vehicle)</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.modify_cost_usd || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, modify_cost_usd: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0.35;
                setForm({...form, modify_cost_usd: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="0.35 oder 0,35"
            />
            <p className="text-xs text-white/40 mt-1">ControlNet (wie Remix)</p>
            <p className="text-xs text-orange-400 mt-2">
              ${form.modify_cost_usd || 0} → €{usdToEur(form.modify_cost_usd || 0)}
            </p>
          </div>

          {/* GPT Prompt-Expansion */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">
              GPT Prompt-Expansion
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.prompt_expansion_cost_usd || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, prompt_expansion_cost_usd: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, prompt_expansion_cost_usd: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="0.003 oder 0,003"
            />
            <p className="text-xs text-white/40 mt-1">Pro Bild-Generierung (nur bei Laien-Text)</p>
            <p className="text-xs text-blue-400 mt-2">
              ${form.prompt_expansion_cost_usd || 0} → €{usdToEur(form.prompt_expansion_cost_usd || 0)}
            </p>
            <p className="text-xs text-white/50 mt-2">
              ℹ️ Nicht bei Remix oder Super-Prompts
            </p>
          </div>
        </div>

        {/* RECHTS: SNAP VERKAUFSPREISE */}
        <div className="space-y-6">
          <h4 className="text-lg font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            SNAP-Kosten (Verkaufspreise)
          </h4>
          
          {/* Avatar Training Verkauf */}
          <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/30 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Avatar Training (SNAPs)</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.avatar_sale_price_eur || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, avatar_sale_price_eur: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, avatar_sale_price_eur: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="2.50 oder 2,50"
            />
            <p className="text-xs text-white/50 mt-1">= €{(parseFloat(form.avatar_sale_price_eur) || 0).toFixed(2)}</p>
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Kosten:</span>
                <span className="text-orange-400">€{usdToEur(form.avatar_cost_usd || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-white/60">Verkauf:</span>
                <span className="text-white">€{(parseFloat(form.avatar_sale_price_eur) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-white/10">
                <span className="text-white">Marge:</span>
                <span className="text-[#10B981]">
                  €{calculateMargin(form.avatar_cost_usd || 0, form.avatar_sale_price_eur || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Shot Verkauf */}
          <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/30 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Shot (Avatar Bild) - SNAPs</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.shot_sale_price_eur || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, shot_sale_price_eur: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, shot_sale_price_eur: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="1.00 oder 1,00"
            />
            <p className="text-xs text-white/50 mt-1">= €{(parseFloat(form.shot_sale_price_eur) || 0).toFixed(2)}</p>
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Kosten:</span>
                <span className="text-orange-400">€{usdToEur(form.shot_cost_usd || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-white/60">Verkauf:</span>
                <span className="text-white">€{(parseFloat(form.shot_sale_price_eur) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-white/10">
                <span className="text-white">Marge:</span>
                <span className="text-[#10B981]">
                  €{calculateMargin(form.shot_cost_usd || 0, form.shot_sale_price_eur || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Remix Verkauf */}
          <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/30 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Remix (Avatar Variation) - SNAPs</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.remix_sale_price_eur || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, remix_sale_price_eur: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, remix_sale_price_eur: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="1.50 oder 1,50"
            />
            <p className="text-xs text-white/50 mt-1">= €{(parseFloat(form.remix_sale_price_eur) || 0).toFixed(2)}</p>
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Kosten:</span>
                <span className="text-orange-400">€{usdToEur(form.remix_cost_usd || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-white/60">Verkauf:</span>
                <span className="text-white">€{(parseFloat(form.remix_sale_price_eur) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-white/10">
                <span className="text-white">Marge:</span>
                <span className="text-[#10B981]">
                  €{calculateMargin(form.remix_cost_usd || 0, form.remix_sale_price_eur || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Modify Verkauf - NEU */}
          <div className="p-4 bg-[#10B981]/5 border border-[#10B981]/30 rounded-xl">
            <label className="text-sm font-bold text-white block mb-2">Modify (Avatar + Vehicle) - SNAPs</label>
            <input
              type="text"
              inputMode="decimal"
              value={String(form.modify_sale_price_eur || '')}
              onChange={(e) => {
                const input = e.target.value.replace(',', '.');
                setForm({...form, modify_sale_price_eur: input});
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value.replace(',', '.')) || 0;
                setForm({...form, modify_sale_price_eur: val});
              }}
              disabled={!editing}
              className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg disabled:opacity-50 focus:border-[#10B981] transition-all"
              placeholder="1.50 oder 1,50"
            />
            <p className="text-xs text-white/50 mt-1">= €{(parseFloat(form.modify_sale_price_eur) || 0).toFixed(2)}</p>
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Kosten:</span>
                <span className="text-orange-400">€{usdToEur(form.modify_cost_usd || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-white/60">Verkauf:</span>
                <span className="text-white">€{(parseFloat(form.modify_sale_price_eur) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base font-bold mt-2 pt-2 border-t border-white/10">
                <span className="text-white">Marge:</span>
                <span className="text-[#10B981]">
                  €{calculateMargin(form.modify_cost_usd || 0, form.modify_sale_price_eur || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* GESAMT-MARGE ÜBERSICHT */}
      <div className="p-6 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/30 rounded-xl">
        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[#10B981]" />
          Gesamt-Marge Übersicht
        </h4>
        
        {/* Einzelne Margen */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="p-4 bg-black/40 rounded-lg">
            <p className="text-xs text-white/60 mb-1">Avatar Training</p>
            <p className="text-2xl font-bold text-[#10B981]">
              €{calculateMargin(form.avatar_cost_usd || 0, form.avatar_sale_price_eur || 0)}
            </p>
            <p className="text-xs text-white/40 mt-1">pro Training</p>
          </div>
          
          <div className="p-4 bg-black/40 rounded-lg">
            <p className="text-xs text-white/60 mb-1">Shot (Avatar Bild)</p>
            <p className="text-2xl font-bold text-[#10B981]">
              €{calculateMargin(form.shot_cost_usd || 0, form.shot_sale_price_eur || 0)}
            </p>
            <p className="text-xs text-white/40 mt-1">pro Shot</p>
          </div>
          
          <div className="p-4 bg-black/40 rounded-lg">
            <p className="text-xs text-white/60 mb-1">Remix (Avatar Variation)</p>
            <p className="text-2xl font-bold text-[#10B981]">
              €{calculateMargin(form.remix_cost_usd || 0, form.remix_sale_price_eur || 0)}
            </p>
            <p className="text-xs text-white/40 mt-1">pro Remix</p>
          </div>
          
          <div className="p-4 bg-black/40 rounded-lg">
            <p className="text-xs text-white/60 mb-1">Modify (Avatar + Vehicle)</p>
            <p className="text-2xl font-bold text-[#10B981]">
              €{calculateMargin(form.modify_cost_usd || 0, form.modify_sale_price_eur || 0)}
            </p>
            <p className="text-xs text-white/40 mt-1">pro Modify</p>
          </div>
          
          {/* GESAMT-MARGE */}
          <div className="p-4 bg-[#10B981]/20 border-2 border-[#10B981] rounded-lg">
            <p className="text-xs text-white/80 mb-1 font-bold">GESAMT-MARGE</p>
            <p className="text-3xl font-bold text-[#10B981]">
              €{(
                parseFloat(calculateMargin(form.avatar_cost_usd || 0, form.avatar_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.shot_cost_usd || 0, form.shot_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.remix_cost_usd || 0, form.remix_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.modify_cost_usd || 0, form.modify_sale_price_eur || 0))
              ).toFixed(2)}
            </p>
            <p className="text-xs text-white/60 mt-1">alle Services</p>
          </div>
        </div>

        {/* Coole Gegenüberstellung */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-black/60 rounded-xl border border-white/10">
          <div className="text-center">
            <p className="text-xs text-white/60 mb-2">Astria Kosten (EUR)</p>
            <p className="text-xl font-bold text-orange-400">
              €{(
                parseFloat(usdToEur(form.avatar_cost_usd || 0)) +
                parseFloat(usdToEur(form.shot_cost_usd || 0)) +
                parseFloat(usdToEur(form.remix_cost_usd || 0)) +
                parseFloat(usdToEur(form.modify_cost_usd || 0))
              ).toFixed(2)}
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-white/60 mb-2">SNAP-Verkauf (EUR)</p>
            <p className="text-xl font-bold text-white">
              €{(
                (parseFloat(form.avatar_sale_price_eur) || 0) +
                (parseFloat(form.shot_sale_price_eur) || 0) +
                (parseFloat(form.remix_sale_price_eur) || 0) +
                (parseFloat(form.modify_sale_price_eur) || 0)
              ).toFixed(2)}
            </p>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-white/60 mb-2">Deine Marge (EUR)</p>
            <p className="text-xl font-bold text-[#10B981]">
              €{(
                parseFloat(calculateMargin(form.avatar_cost_usd || 0, form.avatar_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.shot_cost_usd || 0, form.shot_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.remix_cost_usd || 0, form.remix_sale_price_eur || 0)) +
                parseFloat(calculateMargin(form.modify_cost_usd || 0, form.modify_sale_price_eur || 0))
              ).toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Speichern & Abbrechen Buttons */}
      {editing && (
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all flex items-center gap-2"
            data-testid="save-pricing-settings-button"
          >
            <Save className="w-4 h-4" />
            Einstellungen speichern
          </button>
        </div>
      )}

    </div>
  );
}


// ============================================================================
// PACKAGE CARD
// ============================================================================

function PackageListItem({ package: pkg, pricing, isEditing, onEdit, onCancelEdit, onSave, onDelete }) {
  const [form, setForm] = useState(pkg);

  useEffect(() => {
    setForm(pkg);
  }, [pkg]);

  const handleSave = () => {
    const updates = {};
    Object.keys(form).forEach(key => {
      if (form[key] !== pkg[key] && !['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'calculated_snaps', 'calculated_images', 'calculated_shots', 'api_cost_usd', 'api_cost_eur', 'stripe_fees', 'margin_eur', 'margin_percent'].includes(key)) {
        updates[key] = form[key];
      }
    });

    if (Object.keys(updates).length > 0) {
      onSave(updates);
    } else {
      onCancelEdit();
    }
  };

  const toggleActive = () => {
    onSave({ is_active: !pkg.is_active });
  };

  return (
    <div className={`p-6 bg-black border-2 rounded-xl transition-all ${
      pkg.is_active ? 'border-[#10B981]/30' : 'border-white/10 opacity-60'
    }`}>
      
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h4 className="text-xl font-bold text-white">{pkg.name}</h4>
          {pkg.is_active ? (
            <span className="px-3 py-1 bg-[#10B981]/20 text-[#10B981] rounded-lg text-xs font-bold">AKTIV</span>
          ) : (
            <span className="px-3 py-1 bg-white/10 text-white/40 rounded-lg text-xs font-bold">INAKTIV</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <button
                onClick={toggleActive}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                title={pkg.is_active ? 'Deaktivieren' : 'Aktivieren'}
              >
                {pkg.is_active ? <Eye className="w-5 h-5 text-white/60" /> : <EyeOff className="w-5 h-5 text-white/40" />}
              </button>
              <button
                onClick={onEdit}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
                title="Bearbeiten"
              >
                <Edit2 className="w-5 h-5 text-white/60" />
              </button>
              <button
                onClick={onDelete}
                className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                title="Löschen"
              >
                <Trash2 className="w-5 h-5 text-red-400" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#10B981] text-black rounded-lg hover:bg-[#059669] transition-all flex items-center gap-2 font-bold"
              >
                <Save className="w-4 h-4" />
                Speichern
              </button>
              <button
                onClick={onCancelEdit}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
              >
                Abbrechen
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <PackageEditForm package={form} onChange={setForm} />
      ) : (
        <PackageView package={pkg} />
      )}

    </div>
  );
}


// ============================================================================
// PACKAGE VIEW (Read-only)
// ============================================================================

function PackageView({ package: pkg }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      
      {/* Verkauf */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Verkauf</h5>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-white/60 text-sm">Preis:</span>
            <span className="text-white font-bold text-lg">{pkg.price_eur.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/60 text-sm">Bilder:</span>
            <span className="text-white font-bold">{pkg.calculated_images || pkg.image_count || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/60 text-sm">Avatare:</span>
            <span className="text-white font-bold">{pkg.avatar_count || (pkg.calculated_snaps >= 50 ? 1 : 0)}</span>
          </div>
        </div>
      </div>

      {/* Kosten */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Kosten</h5>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-white/60 text-sm">API-Kosten:</span>
            <span className="text-orange-400 font-bold">{pkg.api_cost_eur?.toFixed(2) || '0.00'} €</span>
          </div>
          <div className="flex justify-between">
            <span className="text-white/60 text-sm">Stripe:</span>
            <span className="text-orange-400 font-bold">{pkg.stripe_fees?.toFixed(2) || '0.00'} €</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-white/10">
            <span className="text-white/60 text-sm">Gesamt:</span>
            <span className="text-red-400 font-bold">{((pkg.api_cost_eur || 0) + (pkg.stripe_fees || 0)).toFixed(2)} €</span>
          </div>
        </div>
      </div>

      {/* Marge */}
      <div>
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Deine Marge</h5>
        <div className="space-y-2">
          <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl">
            <p className="text-[#10B981] text-3xl font-bold mb-1">{pkg.margin_eur?.toFixed(2) || '0.00'} €</p>
            <p className="text-[#10B981]/60 text-sm">{pkg.margin_percent?.toFixed(1) || '0.0'}% Marge</p>
          </div>
          <div className="text-xs text-white/40">
            <p>Pro verkauftem Paket</p>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="col-span-full">
        <h5 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-2">Beschreibung</h5>
        <p className="text-white/80 text-sm">{pkg.description}</p>
      </div>

    </div>
  );
}


// ============================================================================
// PACKAGE EDIT FORM
// ============================================================================

function PackageEditForm({ package: pkg, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      
      <div>
        <label className="text-xs text-white/60 block mb-2">Paket-Name</label>
        <input
          type="text"
          value={pkg.name}
          onChange={(e) => onChange({...pkg, name: e.target.value})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="z.B. Starter"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Preis (€)</label>
        <input
          type="number"
          step="0.01"
          value={pkg.price_eur}
          onChange={(e) => onChange({...pkg, price_eur: parseFloat(e.target.value)})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="9.99"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Anzahl Avatare</label>
        <input
          type="number"
          value={pkg.avatar_count || 0}
          onChange={(e) => onChange({...pkg, avatar_count: parseInt(e.target.value)})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="1"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Anzahl Bilder</label>
        <input
          type="number"
          value={pkg.image_count || 0}
          onChange={(e) => onChange({...pkg, image_count: parseInt(e.target.value)})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="20"
        />
      </div>

      <div className="col-span-full">
        <label className="text-xs text-white/60 block mb-2">Beschreibung</label>
        <textarea
          value={pkg.description}
          onChange={(e) => onChange({...pkg, description: e.target.value})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all resize-none"
          rows={2}
          placeholder="z.B. Perfekt zum Ausprobieren"
        />
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-2">Sortierung</label>
        <input
          type="number"
          value={pkg.sort_order || 0}
          onChange={(e) => onChange({...pkg, sort_order: parseInt(e.target.value)})}
          className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
          placeholder="0"
        />
        <p className="text-xs text-white/40 mt-1">Niedriger = weiter oben</p>
      </div>

    </div>
  );
}


// ============================================================================
// CREATE PACKAGE MODAL
// ============================================================================

function CreatePackageModal({ pricing, onClose, onSave }) {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [form, setForm] = useState({
    name: 'Neues Paket',
    description: 'Beschreibung hier eingeben',
    price_eur: 9.99,
    avatar_count: 1,
    image_count: 20,
    is_active: true,
    sort_order: 0,
    iconType: 'sparkles',
    template: 'classic',
    accentColor: '#10B981',
    isPopular: false,
    useGradient: false,  // NEU: Farbiger Hintergrund
    custom_badge: ''  // FIX: snake_case wie Backend
  });

  const ICON_OPTIONS = [
    { value: 'sparkles', label: '✨ Sparkles' },
    { value: 'star', label: '⭐ Star' },
    { value: 'gem', label: '💎 Gem' },
    { value: 'zap', label: '⚡ Zap' },
    { value: 'camera', label: '📷 Camera' },
    { value: 'award', label: '🏆 Award' }
  ];

  const TEMPLATE_OPTIONS = [
    { value: 'classic', label: 'Classic', description: 'Aktuelles SNAPSHOT Design' },
    { value: 'modern', label: 'Modern', description: 'Horizontal Card' },
    { value: 'minimalist', label: 'Minimalist', description: 'Clean & Text-fokussiert' }
  ];

  const COLOR_PRESETS = [
    { value: '#10B981', label: 'Grün' },
    { value: '#3B82F6', label: 'Blau' },
    { value: '#A855F7', label: 'Purple' },
    { value: '#F97316', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' }
  ];

  const handleSubmit = () => {
    if (!form.name) {
      toast.error('Name ist erforderlich');
      return;
    }
    if (form.price_eur <= 0) {
      toast.error('Preis muss größer als 0 sein');
      return;
    }
    onSave(form);
  };

  // Kosten berechnen
  const calculateCosts = () => {
    if (!pricing) return null;
    
    const avatarCount = parseInt(form.avatar_count) || 0;
    const imageCount = parseInt(form.image_count) || 0;
    const price = parseFloat(form.price_eur) || 0;

    const snapsPerAvatar = pricing.snaps_per_avatar || 50;
    const snapsPerShot = pricing.snaps_per_shot || 1;
    const avatarCostUsd = pricing.avatar_cost_usd || 2.16;
    const shotCostUsd = pricing.shot_cost_usd || 0.255;
    const usdToEur = pricing.usd_to_eur_rate || 0.90;
    const stripePercent = pricing.stripe_fee_percent || 1.5;
    const stripeFixed = pricing.stripe_fee_fixed_eur || 0.25;

    const totalSnaps = (avatarCount * snapsPerAvatar) + Math.ceil(imageCount / 4) * snapsPerShot;
    const shots = Math.floor(imageCount / 4);
    
    const avatarCostEur = avatarCount * avatarCostUsd * usdToEur;
    const shotCostEur = shots * shotCostUsd * usdToEur;
    const apiCostEur = avatarCostEur + shotCostEur;
    
    const stripeFees = (price * stripePercent / 100) + stripeFixed;
    const marginEur = price - apiCostEur - stripeFees;
    const marginPercent = price > 0 ? (marginEur / price * 100) : 0;

    return {
      totalSnaps,
      apiCostEur: apiCostEur.toFixed(2),
      stripeFees: stripeFees.toFixed(2),
      marginEur: marginEur.toFixed(2),
      marginPercent: marginPercent.toFixed(1)
    };
  };

  const costs = calculateCosts();

  // Preview Package für PackageCard
  const previewPackage = {
    id: 'preview',
    name: form.name,
    description: form.description,
    price: parseFloat(form.price_eur) || 0,
    avatarCount: parseInt(form.avatar_count) || 0,  // NEU: avatarCount statt snaps
    imageCount: parseInt(form.image_count) || 0,     // NEU: imageCount statt images
    subtext: form.subtext || '',
    iconType: form.iconType,
    isPopular: form.isPopular,
    useGradient: form.useGradient,  // NEU: Farbiger Hintergrund
    customBadge: form.custom_badge  // FIX: Map snake_case to camelCase für Component
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-black border-2 border-[#10B981]/30 rounded-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto p-6">
        
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Plus className="w-7 h-7 text-[#10B981]" />
            Neues Paket erstellen
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
          >
            <span className="text-white text-xl">×</span>
          </button>
        </div>

        {/* SPLIT SCREEN: Edit Form + Live Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LEFT: Edit Form */}
          <div className="space-y-6">
            
            <div>
              <label className="text-sm font-bold text-white mb-2 block">Paket-Name *</label>
              
              {/* Quick-Select Buttons für Namen */}
              <div className="mb-3">
                <p className="text-xs text-white/50 mb-2">Einstieg:</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Starter', 'Basic', 'Mini', 'Light', 'Intro', 'Essential', 'Quick', 'Spark', 'Bronze'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-white/50 mb-2">Mittel:</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Standard', 'Plus', 'Popular', 'Pro', 'Medium', 'Favorite', 'Silver', 'Boost', 'Flash'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-white/50 mb-2">Premium:</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['Premium', 'Ultimate', 'Elite', 'VIP', 'Max', 'Power', 'Gold', 'Platinum', 'Diamond', 'Supreme', 'Mega', 'Ultra'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                placeholder="Oder eigenen Namen eingeben..."
              />
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Beschreibung (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all resize-none"
                rows={3}
                placeholder="z.B. Perfekt zum Ausprobieren (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Preis (€) * = SNAPs</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={form.price_eur}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setForm({...form, price_eur: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="9.99"
                />
                <p className="text-xs text-[#10B981] mt-1">= {form.price_eur || 0} SNAPs</p>
              </div>
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Sortierung</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.sort_order}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, sort_order: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Avatare</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.avatar_count}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, avatar_count: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Bilder</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.image_count}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, image_count: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Icon</label>
              <select
                value={form.iconType}
                onChange={(e) => setForm({...form, iconType: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              >
                {ICON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Layout-Template</label>
              <select
                value={form.template}
                onChange={(e) => setForm({...form, template: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              >
                {TEMPLATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Akzent-Farbe</label>
              <div className="flex gap-2">
                {COLOR_PRESETS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setForm({...form, accentColor: color.value})}
                    className={`w-12 h-12 rounded-lg border-2 transition ${
                      form.accentColor === color.value ? 'border-white scale-110' : 'border-white/20'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(e) => setForm({...form, isPopular: e.target.checked})}
                  className="w-4 h-4 accent-[#10B981]"
                />
                <span className="text-sm text-white">Bestseller-Badge</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.useGradient}
                  onChange={(e) => setForm({...form, useGradient: e.target.checked})}
                  className="w-4 h-4 accent-[#10B981]"
                />
                <span className="text-sm text-white">Farbiger Hintergrund</span>
              </label>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Custom Badge (optional)</label>
              
              {/* Quick-Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
                {['NEU', 'HOT', 'SALE', '-20%', 'BLACK FRIDAY', 'LIMITED', 'VIP', 'BONUS'].map((badge) => (
                  <button
                    key={badge}
                    type="button"
                    onClick={() => setForm({...form, custom_badge: badge})}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      form.custom_badge === badge
                        ? 'bg-[#10B981] text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {badge}
                  </button>
                ))}
              </div>
              
              <input
                type="text"
                value={form.custom_badge}
                onChange={(e) => setForm({...form, custom_badge: e.target.value})}
                placeholder="Oder eigenen Text eingeben..."
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              />
            </div>

            {costs && (
              <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-[#10B981]" />
                  <h4 className="font-bold text-sm text-white">Kosten & Marge</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-white/70">
                    <span>API-Kosten:</span>
                    <span className="font-medium text-white">€{costs.apiCostEur}</span>
                  </div>
                  <div className="flex justify-between text-white/70">
                    <span>Stripe-Gebühren:</span>
                    <span className="font-medium text-white">€{costs.stripeFees}</span>
                  </div>
                  <div className="border-t border-white/20 pt-2 mt-2"></div>
                  <div className="flex justify-between">
                    <span className="text-white font-medium">Marge:</span>
                    <span className="font-bold text-[#10B981]">€{costs.marginEur} ({costs.marginPercent}%)</span>
                  </div>
                </div>
                {parseFloat(costs.marginPercent) < 20 && (
                  <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-400">
                      Warnung: Marge unter 20%!
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                className="flex-1 px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all"
              >
                Paket erstellen
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
              >
                Abbrechen
              </button>
            </div>
          </div>

          {/* RIGHT: Live Preview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">Live-Vorschau</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`p-2 rounded transition ${
                    previewMode === 'desktop' ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`p-2 rounded transition ${
                    previewMode === 'mobile' ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className={previewMode === 'mobile' ? 'max-w-sm mx-auto' : ''}>
              <PackageCard 
                pkg={previewPackage} 
                template={form.template}
                accentColor={form.accentColor}
                previewMode={true}
              />
            </div>

            <p className="text-xs text-white/50 text-center mt-4">
              So wird das Paket für Kunden angezeigt
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}


// ============================================================================
// EDIT PACKAGE MODAL (mit Live-Vorschau)
// ============================================================================

function EditPackageModal({ pkgData, pricing, onClose, onSave }) {
  const [previewMode, setPreviewMode] = useState('desktop');
  const [form, setForm] = useState({
    name: pkgData.name,
    description: pkgData.description,
    price_eur: pkgData.price_eur,
    avatar_count: pkgData.avatar_count || 1,
    image_count: pkgData.image_count || 0,
    is_active: pkgData.is_active,
    sort_order: pkgData.sort_order || 0,
    icon_type: pkgData.icon_type || 'sparkles',
    template: pkgData.template || 'classic',
    accent_color: pkgData.accent_color || '#10B981',
    is_popular: pkgData.is_popular || false,
    custom_badge: pkgData.custom_badge || ''
  });

  const ICON_OPTIONS = [
    { value: 'sparkles', label: '✨ Sparkles' },
    { value: 'star', label: '⭐ Star' },
    { value: 'gem', label: '💎 Gem' },
    { value: 'zap', label: '⚡ Zap' },
    { value: 'camera', label: '📷 Camera' },
    { value: 'award', label: '🏆 Award' }
  ];

  const TEMPLATE_OPTIONS = [
    { value: 'classic', label: 'Classic', description: 'Aktuelles SNAPSHOT Design' },
    { value: 'modern', label: 'Modern', description: 'Horizontal Card' },
    { value: 'minimalist', label: 'Minimalist', description: 'Clean & Text-fokussiert' }
  ];

  const COLOR_PRESETS = [
    { value: '#10B981', label: 'Grün' },
    { value: '#3B82F6', label: 'Blau' },
    { value: '#A855F7', label: 'Purple' },
    { value: '#F97316', label: 'Orange' },
    { value: '#EF4444', label: 'Rot' }
  ];

  const handleSubmit = () => {
    if (!form.name) {
      toast.error('Name ist erforderlich');
      return;
    }
    if (form.price_eur <= 0) {
      toast.error('Preis muss größer als 0 sein');
      return;
    }
    
    // Nur geänderte Felder senden
    const updates = {};
    Object.keys(form).forEach(key => {
      if (form[key] !== pkgData[key]) {
        updates[key] = form[key];
      }
    });
    
    onSave(updates);
  };

  // Kosten berechnen
  const calculateCosts = () => {
    if (!pricing) return null;
    
    const avatarCount = parseInt(form.avatar_count) || 0;
    const imageCount = parseInt(form.image_count) || 0;
    const price = parseFloat(form.price_eur) || 0;

    const snapsPerAvatar = pricing.snaps_per_avatar || 50;
    const snapsPerShot = pricing.snaps_per_shot || 1;
    const avatarCostUsd = pricing.avatar_cost_usd || 2.16;
    const shotCostUsd = pricing.shot_cost_usd || 0.255;
    const usdToEur = pricing.usd_to_eur_rate || 0.90;
    const stripePercent = pricing.stripe_fee_percent || 1.5;
    const stripeFixed = pricing.stripe_fee_fixed_eur || 0.25;

    const totalSnaps = (avatarCount * snapsPerAvatar) + Math.ceil(imageCount / 4) * snapsPerShot;
    const shots = Math.floor(imageCount / 4);
    
    const avatarCostEur = avatarCount * avatarCostUsd * usdToEur;
    const shotCostEur = shots * shotCostUsd * usdToEur;
    const apiCostEur = avatarCostEur + shotCostEur;
    
    const stripeFees = (price * stripePercent / 100) + stripeFixed;
    const marginEur = price - apiCostEur - stripeFees;
    const marginPercent = price > 0 ? (marginEur / price * 100) : 0;

    return {
      totalSnaps,
      apiCostEur: apiCostEur.toFixed(2),
      stripeFees: stripeFees.toFixed(2),
      marginEur: marginEur.toFixed(2),
      marginPercent: marginPercent.toFixed(1)
    };
  };

  const costs = calculateCosts();

  // Preview Package für PackageCard
  const previewPackage = {
    id: 'preview',
    name: form.name,
    description: form.description,
    price: parseFloat(form.price_eur) || 0,
    avatarCount: parseInt(form.avatar_count) || 0,  // NEU: avatarCount statt snaps
    imageCount: parseInt(form.image_count) || 0,     // NEU: imageCount statt images
    subtext: form.subtext || '',
    iconType: form.icon_type,
    isPopular: form.is_popular,
    useGradient: form.use_gradient,  // NEU: Farbiger Hintergrund
    customBadge: form.custom_badge
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-black border-2 border-[#10B981]/30 rounded-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto p-6">
        
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Edit2 className="w-7 h-7 text-[#10B981]" />
            Paket bearbeiten: {pkgData.name}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
          >
            <span className="text-white text-xl">×</span>
          </button>
        </div>

        {/* SPLIT SCREEN: Edit Form + Live Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LEFT: Edit Form */}
          <div className="space-y-6">
            
            <div>
              <label className="text-sm font-bold text-white mb-2 block">Paket-Name *</label>
              
              {/* Quick-Select Buttons für Namen */}
              <div className="mb-3">
                <p className="text-xs text-white/50 mb-2">Einstieg:</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Starter', 'Basic', 'Mini', 'Light', 'Intro', 'Essential', 'Quick', 'Spark', 'Bronze'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-white/50 mb-2">Mittel:</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['Standard', 'Plus', 'Popular', 'Pro', 'Medium', 'Favorite', 'Silver', 'Boost', 'Flash'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                
                <p className="text-xs text-white/50 mb-2">Premium:</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['Premium', 'Ultimate', 'Elite', 'VIP', 'Max', 'Power', 'Gold', 'Platinum', 'Diamond', 'Supreme', 'Mega', 'Ultra'].map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm({...form, name: name})}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        form.name === name ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
                placeholder="Oder eigenen Namen eingeben..."
              />
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Beschreibung (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all resize-none"
                rows={3}
                placeholder="z.B. Perfekt zum Ausprobieren (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Preis (€) * = SNAPs</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={form.price_eur}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    setForm({...form, price_eur: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="9.99"
                />
                <p className="text-xs text-[#10B981] mt-1">= {form.price_eur || 0} SNAPs</p>
              </div>
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Sortierung</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.sort_order}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, sort_order: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Avatare</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.avatar_count}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, avatar_count: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-white mb-2 block">Bilder</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.image_count}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setForm({...form, image_count: val});
                  }}
                  className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white text-lg focus:border-[#10B981] transition-all"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Icon</label>
              <select
                value={form.icon_type}
                onChange={(e) => setForm({...form, icon_type: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              >
                {ICON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Layout-Template</label>
              <select
                value={form.template}
                onChange={(e) => setForm({...form, template: e.target.value})}
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              >
                {TEMPLATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} - {opt.description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Akzent-Farbe</label>
              <div className="flex gap-2">
                {COLOR_PRESETS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setForm({...form, accent_color: color.value})}
                    className={`w-12 h-12 rounded-lg border-2 transition ${
                      form.accent_color === color.value ? 'border-white scale-110' : 'border-white/20'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_popular}
                  onChange={(e) => setForm({...form, is_popular: e.target.checked})}
                  className="w-4 h-4 accent-[#10B981]"
                />
                <span className="text-sm text-white">Bestseller-Badge</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.use_gradient}
                  onChange={(e) => setForm({...form, use_gradient: e.target.checked})}
                  className="w-4 h-4 accent-[#10B981]"
                />
                <span className="text-sm text-white">Farbiger Hintergrund</span>
              </label>
            </div>

            <div>
              <label className="text-sm font-bold text-white mb-2 block">Custom Badge (optional)</label>
              
              {/* Quick-Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-3">
                {['NEU', 'HOT', 'SALE', '-20%', 'BLACK FRIDAY', 'LIMITED', 'VIP', 'BONUS'].map((badge) => (
                  <button
                    key={badge}
                    type="button"
                    onClick={() => setForm({...form, custom_badge: badge})}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                      form.custom_badge === badge
                        ? 'bg-[#10B981] text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {badge}
                  </button>
                ))}
              </div>
              
              <input
                type="text"
                value={form.custom_badge}
                onChange={(e) => setForm({...form, custom_badge: e.target.value})}
                placeholder="Oder eigenen Text eingeben..."
                className="w-full px-4 py-3 bg-black/60 border-2 border-white/20 rounded-xl text-white focus:border-[#10B981] transition-all"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({...form, is_active: e.target.checked})}
                className="w-4 h-4 accent-[#10B981]"
              />
              <label className="text-sm text-white">Paket aktiv (öffentlich sichtbar)</label>
            </div>

            {costs && (
              <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-[#10B981]" />
                  <h4 className="font-bold text-sm text-white">Kosten & Marge</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-white/70">
                    <span>API-Kosten:</span>
                    <span className="font-medium text-white">€{costs.apiCostEur}</span>
                  </div>
                  <div className="flex justify-between text-white/70">
                    <span>Stripe-Gebühren:</span>
                    <span className="font-medium text-white">€{costs.stripeFees}</span>
                  </div>
                  <div className="border-t border-white/20 pt-2 mt-2"></div>
                  <div className="flex justify-between">
                    <span className="text-white font-medium">Marge:</span>
                    <span className="font-bold text-[#10B981]">€{costs.marginEur} ({costs.marginPercent}%)</span>
                  </div>
                </div>
                {parseFloat(costs.marginPercent) < 20 && (
                  <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-400">
                      Warnung: Marge unter 20%!
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleSubmit}
                className="flex-1 px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all"
              >
                Änderungen speichern
              </button>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-white/10 text-white rounded-xl font-bold hover:bg-white/20 transition-all"
              >
                Abbrechen
              </button>
            </div>
          </div>

          {/* RIGHT: Live Preview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-white">Live-Vorschau</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewMode('desktop')}
                  className={`p-2 rounded transition ${
                    previewMode === 'desktop' ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  className={`p-2 rounded transition ${
                    previewMode === 'mobile' ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className={previewMode === 'mobile' ? 'max-w-sm mx-auto' : ''}>
              <PackageCard 
                pkg={previewPackage} 
                template={form.template}
                accentColor={form.accent_color}
                previewMode={true}
              />
            </div>

            <p className="text-xs text-white/50 text-center mt-4">
              So wird das Paket für Kunden angezeigt
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}
